import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataProvider, useNotify } from 'react-admin';
import { useLocation } from 'react-router';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import type {
  TWidget,
  TGetDashboardByIdResponse,
} from '../../../utility/types/dataProvider';
import type { TDashboardFilters } from '../Dashboard';
import { getInitDashboardOrWidgetFiltersFromModuleDynaFields } from 'components/Dashboard/shared/utils';
import { hasProDataInsightsPrivilege } from 'utility/privileges';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { z } from 'zod';
import type { AssetViewContextType } from 'screens/editorial/asset/context/AssetViewContextProvider';
import { DASHBOARD_RESOURCE } from '../custom-dashboards/utils/constants';

// --------------------------------------------------------------
// Dashboard Response Schema Validation
// --------------------------------------------------------------

const DashboardWidgetSchema = z.object({
  id: z.string(),
  title: z.string(),
  type: z.string(),
  app: z.string(),
  module: z
    .object({
      id: z.string(),
      title: z.string(),
      app: z.string(),
      parameters: z.array(z.any()).optional().or(z.record(z.any(), z.any()).optional()),
      data: z.record(z.any(), z.any()),
      display: z.object({
        options: z.array(z.any()),
        default: z.string().optional(),
        name: z.string().optional(),
        type: z.string().optional(),
      }),
    })
    .nullable(),
  restrictions: z.any().optional(),
  parameters: z.record(z.any(), z.any()).optional().or(z.array(z.any()).optional()),
  display: z
    .object({
      view: z.string(),
      options: z.any(),
      helpText: z.string().optional(),
    })
    .optional(),
  advanced_required: z.boolean().optional(),
  refresh: z.number().optional(),
  updated: z.string().optional(),
});

const DashboardDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  type: z.string().optional(),
  public: z.boolean().optional(),
  location: z
    .object({
      app: z.string(),
      page: z.string(),
      priority: z.number(),
    })
    .optional(),
  advanced_required: z.boolean().optional(),
  updated: z.string().optional(),
  restrictions: z.any().optional(),
  grid: z.string(),
  layout: z
    .array(z.array(DashboardWidgetSchema))
    // If layout is empty, it means the user doesn't have permission to view data insights.
    // This will be handled with `lacksUserPermissions`.
    // Also handled for custom dashboards view (/dashboards/<dashboard id>/view)
    .or(z.array(z.array(DashboardWidgetSchema)).length(0)),
  filters: z.array(z.any()).optional(),
});

const DashboardResponseSchema = z.object({
  status: z.string(),
  data: DashboardDataSchema,
});

/**
 * Validates dashboard response data against the expected schema
 * @param data - The response data to validate
 * @returns Validation result with success boolean and either data or error
 */
const validateDashboardResponse = (data: unknown) => {
  return DashboardResponseSchema.safeParse(data);
};

type UseDashboardParams = {
  app: string;
  assetUUID?: string;
  isChild?: boolean;
  setStatsMenuListWidgetIds?: AssetViewContextType['setStatsMenuListWidgetIds'];
};

export const useDashboard = ({
  app,
  assetUUID,
  isChild = false,
  setStatsMenuListWidgetIds,
}: UseDashboardParams) => {
  const dataProvider = useDataProvider();
  const { sessionConfig } = useCosmos();
  const { queryClient } = useCosmosQueryClient();
  const notify = useNotify();
  const location = useLocation();
  const isAssetDashboard = useMemo(() => !!assetUUID, [assetUUID]);
  const hasProDataInsights = useMemo(() => {
    return hasProDataInsightsPrivilege(sessionConfig);
  }, [sessionConfig]);
  const isKpiBarDashboard = useMemo(() => isChild && !assetUUID, [isChild, assetUUID]);
  const [dashboardFilters, setDashboardFilters] = useState<TDashboardFilters>();
  const [error, setError] = useState<boolean>();
  const initDashboardFiltersRef = useRef<TDashboardFilters | null>(null);

  // Invalidate queries when entering view mode to ensure fresh data
  // This handles the case where the user switches from edit to view mode
  useEffect(() => {
    if (app && location.pathname.includes('/dashboards/view/')) {
      // Invalidate both view and edit queries for this dashboard
      queryClient.invalidateQueries({
        queryKey: ['dashboard', 'getDashboardById', app],
      });
      queryClient.invalidateQueries({
        queryKey: [DASHBOARD_RESOURCE, 'getDashboardById', app],
      });
    }
  }, [app, location.pathname, queryClient]);

  // Fetch the dashboard configuration. WidgetCard.tsx fetches the widget data.
  const {
    data: dashboardData,
    isLoading,
    isError,
    isSuccess,
  } = useQuery({
    queryKey: ['dashboard', 'getDashboardById', app, assetUUID, isKpiBarDashboard],
    queryFn: async () => {
      const response = await dataProvider.getDashboardById({
        id: app,
      });

      // Validate response schema
      const validationResult = validateDashboardResponse(response);

      if (!validationResult.success) {
        console.error('Dashboard response validation failed:', {
          errors: validationResult.error.issues,
          response,
        });
        setError(true);
        throw new Error(
          `Dashboard data validation failed: ${validationResult.error.issues
            .map((e) => `${e.path.join('.')}: ${e.message}`)
            .join(', ')}`,
        );
      }

      // Additional check for layout array (belt and suspenders)
      if (!Array.isArray(response.data.layout)) {
        setError(true);
        console.error('Dashboard layout data is malformed', { response });
        throw new Error('Dashboard layout must be an array');
      }

      return response;
    },
    retry: (failureCount: number, error: any) => {
      if (error.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,
    placeholderData: (previousData) => previousData,
  });

  // Handle success; and setting the stats menu list for editorial asset dashboards.
  useEffect(() => {
    if (dashboardData && isSuccess) {
      setError(false);

      const assetDashboardWidgets = dashboardData?.data?.layout
        ?.flat?.()
        .map((widget: TWidget) => ({ id: widget.id, title: widget.title }));
      if (isAssetDashboard && !!setStatsMenuListWidgetIds) {
        setStatsMenuListWidgetIds(assetDashboardWidgets);
      }
    }
  }, [dashboardData, isAssetDashboard, setStatsMenuListWidgetIds, isSuccess]);

  // Handle errors.
  useEffect(() => {
    if (isError) {
      setError(true);
      notify('Unable to fetch the current dashboard.', { type: 'error' });
    }
  }, [isError, notify]);

  // Get all default values for dashboard filters form.
  useEffect(() => {
    if (!dashboardData) return;
    // Only initialize dashboardFilters if it hasn't been set yet.
    if (!dashboardFilters) {
      if (dashboardData?.data && dashboardData?.data?.filters) {
        const initFilters = getInitDashboardOrWidgetFiltersFromModuleDynaFields(
          dashboardData.data.filters,
        );
        initDashboardFiltersRef.current = initFilters;
        setDashboardFilters(initFilters);
      } else if (dashboardData && dashboardData?.filters) {
        const initFilters = getInitDashboardOrWidgetFiltersFromModuleDynaFields(
          dashboardData.filters,
        );
        initDashboardFiltersRef.current = initFilters;
        setDashboardFilters(initFilters);
      } else {
        // Dashboard has no filter configuration (e.g. custom dashboards).
        // Initialize to an empty object so areFiltersReady becomes true and the dashboard can render.
        setDashboardFilters({} as TDashboardFilters);
      }
    }
  }, [dashboardData, dashboardFilters]);

  // Dashboard data.
  const dashboard: TGetDashboardByIdResponse['data'] = useMemo(() => {
    return dashboardData?.data;
  }, [dashboardData?.data]);

  // Columns for the dashboard layout.
  const columns = useMemo(() => dashboard?.grid?.split('-'), [dashboard]);
  const isDashboardMasked = useMemo(
    () => !!dashboard?.advanced_required && !hasProDataInsights,
    [dashboard, hasProDataInsights],
  );

  // Determine if the user lacks permissions based on dashboard layout.
  const lacksUserPermissions = useMemo(() => {
    // Custom dashboards could potentially have an empty layout, this opens the flow.
    const isCustomDashboardView = location.pathname.match(/\/dashboards\/view\/[^/]+/);
    return (
      dashboard &&
      !isCustomDashboardView &&
      (!dashboard?.layout || dashboard?.layout?.length === 0)
    );
  }, [dashboard, location]);

  // Filters are considered ready once initialization has completed (dashboardFilters is no longer undefined).
  // Dashboards without filter config initialize to {} which is still a valid initialized state.
  const areFiltersReady = useMemo(
    () => dashboardFilters !== undefined,
    [dashboardFilters],
  );

  // Wait for essential data before rendering
  const isReadyToRender = useMemo(
    () =>
      dashboard && columns && typeof isDashboardMasked === 'boolean' && areFiltersReady,
    [dashboard, columns, isDashboardMasked, areFiltersReady],
  );

  return {
    dashboard,
    dashboardFilters,
    setDashboardFilters,
    columns,
    isDashboardMasked,
    lacksUserPermissions,
    areFiltersReady,
    isReadyToRender,
    isLoading,
    isError,
    error,
    hasProDataInsights,
    isAssetDashboard,
    isKpiBarDashboard,
    initDashboardFiltersRef,
  };
};
